import React from 'react';
import {
  ClockIcon,
  UserGroupIcon,
  MapPinIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  MoonIcon,
  SunIcon,
  MagnifyingGlassIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  BoltIcon,
  ShieldCheckIcon,
  EyeIcon,
  HandRaisedIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';

export default function ClockInHelp() {
  return (
    <div className="bg-gradient-to-br from-blue-50 to-cyan-100 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-4 rounded-full shadow-lg">
              <ClockIcon className="h-10 w-10 text-white" />
            </div>
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Clock In Management
          </h1>
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-3 rounded-full inline-block mb-6">
            <span className="text-xl font-semibold">Real-Time Workforce Time Tracking</span>
          </div>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Experience intelligent workforce management with GPS-powered time tracking,
            real-time status monitoring, and comprehensive clock-in/out management for
            optimal operational efficiency and compliance.
          </p>
        </div>

        {/* Innovation Highlights */}
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 mb-8 text-white">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">Why Clock In Management is Revolutionary</h2>
            <p className="text-blue-100 text-lg">Transforming workforce time tracking with intelligent automation</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-white bg-opacity-20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <BoltIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Real-Time Synchronization</h3>
              <p className="text-blue-100">Instant updates across all devices with live status monitoring and automatic data synchronization</p>
            </div>

            <div className="text-center">
              <div className="bg-white bg-opacity-20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPinIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">GPS Location Intelligence</h3>
              <p className="text-blue-100">Precise geolocation tracking for all clock-in/out and break activities with location validation</p>
            </div>

            <div className="text-center">
              <div className="bg-white bg-opacity-20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheckIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Dual-Permission Architecture</h3>
              <p className="text-blue-100">Intelligent permission system distinguishing between self-management and administrative staff control</p>
            </div>
          </div>
        </div>

        {/* Core System Overview */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <UserGroupIcon className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-semibold text-gray-900">The Complete Time Tracking Ecosystem</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Real-Time Monitoring */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
              <div className="flex items-center mb-4">
                <EyeIcon className="h-6 w-6 text-blue-600 mr-3" />
                <h3 className="text-xl font-semibold text-blue-800">Live Status Monitoring</h3>
              </div>
              <p className="text-blue-700 mb-4">Real-time visibility into workforce attendance with instant status updates</p>
              <ul className="space-y-2 text-blue-600">
                <li>• Live clock-in/out status</li>
                <li>• Break status tracking</li>
                <li>• Automatic refresh cycles</li>
                <li>• Real-time staff counts</li>
              </ul>
            </div>

            {/* GPS Integration */}
            <div className="bg-gradient-to-br from-cyan-50 to-teal-50 border-l-4 border-cyan-500 p-6 rounded-r-lg">
              <div className="flex items-center mb-4">
                <MapPinIcon className="h-6 w-6 text-cyan-600 mr-3" />
                <h3 className="text-xl font-semibold text-cyan-800">Location Intelligence</h3>
              </div>
              <p className="text-cyan-700 mb-4">GPS-powered location tracking for all time-related activities</p>
              <ul className="space-y-2 text-cyan-600">
                <li>• Geolocation capture</li>
                <li>• Location validation</li>
                <li>• Audit trail creation</li>
                <li>• Compliance verification</li>
              </ul>
            </div>

            {/* Permission Management */}
            <div className="bg-gradient-to-br from-teal-50 to-green-50 border-l-4 border-teal-500 p-6 rounded-r-lg">
              <div className="flex items-center mb-4">
                <ShieldCheckIcon className="h-6 w-6 text-teal-600 mr-3" />
                <h3 className="text-xl font-semibold text-teal-800">Smart Permissions</h3>
              </div>
              <p className="text-teal-700 mb-4">Intelligent permission architecture for different user roles</p>
              <ul className="space-y-2 text-teal-600">
                <li>• Self-service clocking</li>
                <li>• Administrative controls</li>
                <li>• Role-based access</li>
                <li>• Secure API endpoints</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Workflow Diagram */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Clock In Management Workflow</h2>

          <div className="relative">
            {/* Central Hub */}
            <div className="flex justify-center mb-8">
              <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-8 py-4 rounded-full shadow-lg">
                <div className="flex items-center">
                  <ClockIcon className="h-6 w-6 mr-3" />
                  <span className="text-lg font-semibold">Clock In Dashboard</span>
                </div>
              </div>
            </div>

            {/* Workflow Branches */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Current User Flow */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <div className="bg-blue-500 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3">
                    <span className="font-bold">1</span>
                  </div>
                  <h3 className="text-xl font-semibold text-blue-800">Current User Actions</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <ArrowPathIcon className="h-5 w-5 text-blue-600 mr-3" />
                    <span className="text-blue-700">Clock In/Out with GPS tracking</span>
                  </div>
                  <div className="flex items-center">
                    <ArrowPathIcon className="h-5 w-5 text-blue-600 mr-3" />
                    <span className="text-blue-700">Break start/end management</span>
                  </div>
                  <div className="flex items-center">
                    <ArrowPathIcon className="h-5 w-5 text-blue-600 mr-3" />
                    <span className="text-blue-700">Automatic pay calculations</span>
                  </div>
                  <div className="flex items-center">
                    <ArrowPathIcon className="h-5 w-5 text-blue-600 mr-3" />
                    <span className="text-blue-700">Late arrival detection</span>
                  </div>
                </div>
              </div>

              {/* Admin Flow */}
              <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 border-2 border-cyan-200 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <div className="bg-cyan-500 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3">
                    <span className="font-bold">2</span>
                  </div>
                  <h3 className="text-xl font-semibold text-cyan-800">Administrative Controls</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <ArrowPathIcon className="h-5 w-5 text-cyan-600 mr-3" />
                    <span className="text-cyan-700">Manage all staff clocking</span>
                  </div>
                  <div className="flex items-center">
                    <ArrowPathIcon className="h-5 w-5 text-cyan-600 mr-3" />
                    <span className="text-cyan-700">Bulk clock-out operations</span>
                  </div>
                  <div className="flex items-center">
                    <ArrowPathIcon className="h-5 w-5 text-cyan-600 mr-3" />
                    <span className="text-cyan-700">Override break management</span>
                  </div>
                  <div className="flex items-center">
                    <ArrowPathIcon className="h-5 w-5 text-cyan-600 mr-3" />
                    <span className="text-cyan-700">Real-time status monitoring</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Data Flow */}
            <div className="flex justify-center mt-8">
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-full shadow-lg">
                <div className="flex items-center">
                  <BoltIcon className="h-5 w-5 mr-2" />
                  <span className="font-semibold">Real-Time Data Synchronization</span>
                </div>
              </div>
            </div>

            {/* Data Destinations */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="bg-white border-2 border-gray-200 rounded-lg p-4 text-center">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2">
                  <ClockIcon className="h-6 w-6 text-blue-600" />
                </div>
                <h4 className="font-semibold text-gray-900">Time Records</h4>
                <p className="text-sm text-gray-600">Complete attendance logs</p>
              </div>
              <div className="bg-white border-2 border-gray-200 rounded-lg p-4 text-center">
                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2">
                  <MapPinIcon className="h-6 w-6 text-green-600" />
                </div>
                <h4 className="font-semibold text-gray-900">GPS Audit Trail</h4>
                <p className="text-sm text-gray-600">Location verification data</p>
              </div>
              <div className="bg-white border-2 border-gray-200 rounded-lg p-4 text-center">
                <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2">
                  <CurrencyDollarIcon className="h-6 w-6 text-purple-600" />
                </div>
                <h4 className="font-semibold text-gray-900">Payroll Integration</h4>
                <p className="text-sm text-gray-600">Automated wage calculations</p>
              </div>
            </div>
          </div>
        </div>

        {/* Key Features Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Real-Time Features */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <BoltIcon className="h-6 w-6 text-blue-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Real-Time Capabilities</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Live Status Updates:</strong> Instant synchronization across all devices with automatic refresh cycles</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>GPS Location Capture:</strong> Precise geolocation data captured for every clock-in/out and break action</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Automatic Calculations:</strong> Real-time pay calculations, hours worked, and break deductions</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Status Monitoring:</strong> Live dashboard showing current clock-in counts and staff availability</span>
              </li>
            </ul>
          </div>

          {/* Permission Architecture */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <ShieldCheckIcon className="h-6 w-6 text-blue-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Intelligent Permission System</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Self-Service Clocking:</strong> Employees can manage their own time tracking with full GPS integration</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Administrative Override:</strong> Managers can clock in/out staff members and manage breaks remotely</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Different Endpoints:</strong> Separate API endpoints for self-service vs administrative actions</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Visual Distinction:</strong> Different button styles and colors for current user vs other staff actions</span>
              </li>
            </ul>
          </div>

          {/* Mobile Optimization */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <DevicePhoneMobileIcon className="h-6 w-6 text-blue-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Mobile-First Design</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Responsive Interface:</strong> Optimized layouts for mobile devices with touch-friendly controls</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Card-Based Layout:</strong> Mobile-optimized cards replacing desktop tables for better usability</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Compact Actions:</strong> Circular icon buttons for space-efficient mobile interactions</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>GPS Integration:</strong> Native mobile GPS access for accurate location tracking on all devices</span>
              </li>
            </ul>
          </div>

          {/* Bulk Operations */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <HandRaisedIcon className="h-6 w-6 text-blue-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Bulk Management Tools</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Clock Out All:</strong> Emergency bulk clock-out functionality for end-of-day operations</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Search & Filter:</strong> Real-time search by staff name or role for quick staff location</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Status Overview:</strong> Live counters showing total clocked-in staff and current status</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Administrative Controls:</strong> Manager-level bulk operations with confirmation safeguards</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Innovation Impact */}
        <div className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-2xl p-8 mb-8 text-white">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">The Impact of Intelligent Clock In Management</h2>
            <p className="text-cyan-100 text-lg">Revolutionizing workforce time tracking and attendance management</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">95%</div>
              <p className="text-cyan-100">Reduction in time tracking errors</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">80%</div>
              <p className="text-cyan-100">Faster end-of-day processing</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">100%</div>
              <p className="text-cyan-100">GPS-verified attendance records</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">60%</div>
              <p className="text-cyan-100">Reduction in administrative overhead</p>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Clock In Management Best Practices</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📱 Mobile Operations</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Enable GPS permissions for accurate tracking</li>
                <li>• Keep devices charged during work hours</li>
                <li>• Test GPS functionality in different locations</li>
                <li>• Use mobile data for real-time synchronization</li>
                <li>• Regularly clear app cache for optimal performance</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">👥 Staff Management</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Train staff on proper clock-in/out procedures</li>
                <li>• Monitor GPS accuracy for location verification</li>
                <li>• Use bulk operations for end-of-day efficiency</li>
                <li>• Review attendance patterns for scheduling insights</li>
                <li>• Address technical issues promptly</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🔧 Administrative Controls</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Use Clock Out All for emergency situations</li>
                <li>• Monitor real-time staff counts regularly</li>
                <li>• Review GPS data for attendance verification</li>
                <li>• Maintain accurate staff role assignments</li>
                <li>• Generate reports from time tracking data</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📊 Data Management</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Regularly backup time tracking data</li>
                <li>• Audit GPS locations for accuracy</li>
                <li>• Monitor system performance metrics</li>
                <li>• Clean up old records periodically</li>
                <li>• Integrate with payroll systems</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🚨 Emergency Procedures</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Use Clock Out All for system shutdowns</li>
                <li>• Have backup clocking procedures ready</li>
                <li>• Document emergency override procedures</li>
                <li>• Train staff on manual clocking alternatives</li>
                <li>• Maintain paper-based backup records</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📈 Performance Optimization</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Monitor GPS accuracy across locations</li>
                <li>• Analyze clock-in/out patterns</li>
                <li>• Optimize bulk operations timing</li>
                <li>• Review mobile app performance</li>
                <li>• Update system based on user feedback</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}